import { Switch, Route, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { JobWatcherProvider } from "@/lib/jobWatcher";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { NetworkStatusBanner } from "@/components/NetworkStatusBanner";
import { SuggestLanguageBanner } from "@/components/SuggestLanguageBanner";
import { Suspense, lazy, useEffect, useState } from "react";
import i18n from "./i18n";

import LandingPage from "./pages/landing";

const WorkspacesPage = lazy(() => import("./pages/workspaces"));
const WorkspaceOverview = lazy(() => import("./pages/workspaces/[id]"));
const BrandBiblePage = lazy(() => import("./pages/workspaces/[id]/brand"));
const SearchPage = lazy(() => import("./pages/workspaces/[id]/search"));
const ResultsPage = lazy(() => import("./pages/workspaces/[id]/results"));
const BookmarksPage = lazy(() => import("./pages/workspaces/[id]/bookmarks"));
const BriefsPage = lazy(() => import("./pages/workspaces/[id]/briefs"));
const TeamPage = lazy(() => import("./pages/workspaces/[id]/team"));
const LoginPage = lazy(() => import("./pages/login"));
const RegisterPage = lazy(() => import("./pages/register"));
const InvitePage = lazy(() => import("./pages/invite/[token]"));
const ForgotPasswordPage = lazy(() => import("./pages/forgot-password"));
const ResetPasswordPage = lazy(() => import("./pages/reset-password"));
const SettingsPage = lazy(() => import("./pages/settings"));
const ProfilePage = lazy(() => import("./pages/profile"));
const BillingPage = lazy(() => import("./pages/billing"));
const AdminOverviewPage = lazy(() => import("./pages/admin/index"));
const AdminUsersPage = lazy(() => import("./pages/admin/users"));
const AdminWorkspacesPage = lazy(() => import("./pages/admin/workspaces"));
const AdminFeedbackPage = lazy(() => import("./pages/admin/feedback"));
const AdminAuditLogPage = lazy(() => import("./pages/admin/audit-log"));
const AdminJobsPage = lazy(() => import("./pages/admin/jobs"));
const AdminSettingsPage = lazy(() => import("./pages/admin/settings"));
const AnalyticsPage = lazy(() => import("./pages/analytics"));
const TermsPage = lazy(() => import("./pages/terms"));
const PrivacyPage = lazy(() => import("./pages/privacy"));
const SupportPage = lazy(() => import("./pages/support"));
const NotFound = lazy(() => import("./pages/not-found"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function computeRouterBase(): string {
  if (typeof window === "undefined") return BASE;
  const path = window.location.pathname;
  const stripped = BASE && path.startsWith(BASE) ? path.slice(BASE.length) : path;
  if (stripped === "/es" || stripped.startsWith("/es/")) return `${BASE}/es`;
  return BASE;
}

const ROUTER_BASE = computeRouterBase();

type AuthUser = { id: number; email: string; displayName: string | null; role: string; locale?: string; createdAt: string | null } | null;

function useAuthUser() {
  return useQuery<AuthUser>({
    queryKey: ["auth-me"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/auth/me`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

function LocaleSync({ user, children }: { user: AuthUser; children?: React.ReactNode }) {
  const [synced, setSynced] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      if (user?.locale) {
        if (i18n.language !== user.locale) {
          await i18n.changeLanguage(user.locale);
        }
        localStorage.setItem("viralens_lang", user.locale);
      }
      if (!cancelled) setSynced(true);
    };
    sync();
    return () => { cancelled = true; };
  }, [user?.locale]);
  if (!synced) return <LoadingSpinner />;
  return <>{children}</>;
}

function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-[#07070a] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-vl-deep/25 border-t-vl-deep rounded-full animate-spin" />
    </div>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading } = useAuthUser();

  if (isLoading) return <LoadingSpinner />;
  if (!user) return <Redirect to="/login" />;

  return <LocaleSync user={user}>{children}</LocaleSync>;
}

function LandingOrDashboard() {
  const { data: user, isLoading } = useAuthUser();

  if (isLoading) return <LoadingSpinner />;
  if (user) return <LocaleSync user={user}><Redirect to="/workspaces" /></LocaleSync>;

  return <LandingPage />;
}

function SettingsRoute() {
  const { data: user, isLoading } = useAuthUser();
  if (isLoading) return <LoadingSpinner />;
  if (!user || user.role !== "admin") return <Redirect to="/workspaces" />;
  return <SettingsPage />;
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading } = useAuthUser();

  if (isLoading) return <LoadingSpinner />;
  if (!user) return <Redirect to="/login" />;
  if (user.role !== "admin") return <Redirect to="/workspaces" />;

  return <>{children}</>;
}

function Router() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Switch>
        <Route path="/" component={LandingOrDashboard} />
        <Route path="/login" component={LoginPage} />
        <Route path="/register" component={RegisterPage} />
        <Route path="/invite/:token" component={InvitePage} />
        <Route path="/forgot-password" component={ForgotPasswordPage} />
        <Route path="/reset-password" component={ResetPasswordPage} />
        <Route path="/terms" component={TermsPage} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/support" component={SupportPage} />

        <Route path="/admin">
          {() => (
            <AdminGuard>
              <AdminOverviewPage />
            </AdminGuard>
          )}
        </Route>
        <Route path="/admin/users">
          {() => (
            <AdminGuard>
              <AdminUsersPage />
            </AdminGuard>
          )}
        </Route>
        <Route path="/admin/workspaces">
          {() => (
            <AdminGuard>
              <AdminWorkspacesPage />
            </AdminGuard>
          )}
        </Route>
        <Route path="/admin/feedback">
          {() => (
            <AdminGuard>
              <AdminFeedbackPage />
            </AdminGuard>
          )}
        </Route>
        <Route path="/admin/audit-log">
          {() => (
            <AdminGuard>
              <AdminAuditLogPage />
            </AdminGuard>
          )}
        </Route>
        <Route path="/admin/jobs">
          {() => (
            <AdminGuard>
              <AdminJobsPage />
            </AdminGuard>
          )}
        </Route>
        <Route path="/admin/settings">
          {() => (
            <AdminGuard>
              <AdminSettingsPage />
            </AdminGuard>
          )}
        </Route>

        <Route path="/*">
          {() => (
            <AuthGuard>
              <JobWatcherProvider>
                <Switch>
                  <Route path="/workspaces" component={WorkspacesPage} />
                  <Route path="/workspaces/:id" component={WorkspaceOverview} />
                  <Route path="/workspaces/:id/brand" component={BrandBiblePage} />
                  <Route path="/workspaces/:id/search" component={SearchPage} />
                  <Route path="/workspaces/:id/results/:jobId" component={ResultsPage} />
                  <Route path="/workspaces/:id/bookmarks" component={BookmarksPage} />
                  <Route path="/workspaces/:id/briefs" component={BriefsPage} />
                  <Route path="/workspaces/:id/team" component={TeamPage} />
                  <Route path="/analytics" component={AnalyticsPage} />
                  <Route path="/settings" component={SettingsRoute} />
                  <Route path="/profile" component={ProfilePage} />
                  <Route path="/billing" component={BillingPage} />
                  <Route component={NotFound} />
                </Switch>
              </JobWatcherProvider>
            </AuthGuard>
          )}
        </Route>
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <NetworkStatusBanner />
          <WouterRouter base={ROUTER_BASE}>
            <Router />
          </WouterRouter>
          <SuggestLanguageBanner />
          <Toaster />
          <SonnerToaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
